import React, { ReactNode, useState } from "react";
import { Flex, Text } from "@chakra-ui/react";
import PathPickBasketCheckCard from "@/features/pathpick/features/basket/components/sync/PathPickBasketCheckCard";
import CustomApprovalModal from "@/shared/components/custom-modal";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  PathPickBasketSelection,
  PathPickFileRequestKind,
} from "@/features/pathpick/features/basket/types";
import { PathPickSmartStoreInfoResponse } from "@/features/pathpick/features/basket/types/response";
import { PathPickProductKey } from "@/features/pathpick/types";
import {
  getMissingFileRequestInfoLabelKeys,
  getRequestableSelections,
  isFileRequestInfoComplete,
  isSelectionRequestable,
} from "@/features/pathpick/features/basket/utils";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  // 선택 상태 전체를 받는다 — 대상/차감 수가 고른 양식에 따라 달라지므로(양식별 처리 상태로
  // 재신청 대상을 가린다) 개수만 받으면 모달 안에서 다시 계산할 수 없다.
  selectedProducts: Map<PathPickProductKey, PathPickBasketSelection>;
  // 스마트스토어 양식에 필요한 기본 정보가 채워졌는지 안내하는 데 쓴다(신청 관문 판정은 호출부 담당).
  smartStoreInfo: PathPickSmartStoreInfoResponse | null | undefined;
  // 기본 정보 관문을 거쳐 이 모달로 되돌아왔을 때 이전에 고른 양식을 복원한다
  initialFormats?: PathPickFileRequestKind[];
  isLoading: boolean;
  // 선택한 파일 종류를 그대로 신청 요청 종류로 넘긴다
  // (스마트스토어 양식에 필요한 기본 정보 검사는 호출부의 관문이 담당한다)
  onConfirm: (kinds: PathPickFileRequestKind[]) => void;
}

// KREAM 파일은 확정 수량 기준이라 현재는 주문 상세에서만 신청한다.
// 담은 상품에서 다시 열려면 이 플래그를 true로 되돌리고, KREAM 이동 안내 박스와
// description / select_description / deduction_description 문구를 KREAM 포함 버전으로 되돌리면 된다.
// (선택 상태는 배열 그대로라 KREAM 관련 계산·안내 코드는 손댈 필요가 없다.)
const IS_KREAM_SELECTABLE = false;

// 처음 열 때 선택된 양식 — 고를 수 없는 양식을 기본값에 넣어두면 안 된다.
const DEFAULT_FORMATS: PathPickFileRequestKind[] = IS_KREAM_SELECTABLE
  ? ["NAVER_EXCEL", "KREAM_EXCEL"]
  : ["NAVER_EXCEL"];

// 선택 조합에 따라 달라지는 안내 박스 — 제목 + 본문 두 줄 구조를 여러 경우가 공유한다.
const NoticeBox = ({
  title,
  body,
  tone,
  footer,
}: {
  title: string;
  body: string;
  tone: "neutral" | "warning" | "warningSubtle" | "danger";
  // 본문 아래에 덧붙는 블록(예: 어디서 신청하는지 짚어주는 박스)
  footer?: ReactNode;
}) => {
  const palette = {
    neutral: {
      border: "none",
      background: "#F7F9FA",
      title: "#444440",
      body: "#8F8F8C",
    },
    warning: {
      border: "0.5px solid #F5C79B",
      background: "#FFF7ED",
      title: "#B27700",
      body: "#B27700",
    },
    // 노란 배경이되 글자는 기본색 — 노란 박스가 연달아 뜰 때 전부 앰버면 읽기 힘들다.
    warningSubtle: {
      border: "0.5px solid #F5C79B",
      background: "#FFF7ED",
      title: "#1D1D1B",
      body: "#8F8F8C",
    },
    danger: {
      border: "0.5px solid #EE7D7A",
      background: "#FFEEEE",
      title: "#CC1D1D",
      body: "#CC1D1D",
    },
  }[tone];

  return (
    <Flex
      flexDirection={"column"}
      gap={"4px"}
      padding={"12px 16px"}
      borderRadius={"8px"}
      border={palette.border}
      background={palette.background}
    >
      <Text textStyle={"body-sm-bold"} color={palette.title}>
        {title}
      </Text>
      <Text textStyle={"body-sm-medium"} color={palette.body}>
        {body}
      </Text>
      {footer}
    </Flex>
  );
};

// 상품 등록 파일 신청 모달 — 생성할 양식(스마트스토어/KREAM)을 골라 신청한다.
function PathPickBasketFileRequestModal({
  isOpen,
  onClose,
  selectedProducts,
  smartStoreInfo,
  initialFormats,
  isLoading,
  onConfirm,
}: Props) {
  const { t } = useI18nHelper();
  // 모달은 열 때마다 새로 마운트되므로, 관문에서 돌아온 경우에만 이전 선택을 초기값으로 받는다.
  // KREAM이 비활성인 동안에는 초기값에서도 걸러낸다 — 예전 intent가 남아 있으면
  // 화면에는 안 보이는데 신청 payload에만 섞여 들어간다.
  const [kinds, setKinds] = useState<PathPickFileRequestKind[]>(() =>
    (initialFormats ?? DEFAULT_FORMATS).filter(
      (kind) => IS_KREAM_SELECTABLE || kind !== "KREAM_EXCEL"
    )
  );

  const toggleKind = (kind: PathPickFileRequestKind) =>
    setKinds((previous) =>
      previous.includes(kind)
        ? previous.filter((item) => item !== kind)
        : [...previous, kind]
    );

  const isKreamSelected = kinds.includes("KREAM_EXCEL");
  const isSmartStoreSelected = kinds.includes("NAVER_EXCEL");

  const selections = Array.from(selectedProducts.values());
  const selectedCount = selections.length;
  const kreamMatchedCount = selections.filter(
    ({ isKreamMatched }) => isKreamMatched
  ).length;
  const kreamExcludedCount = selectedCount - kreamMatchedCount;

  // 고른 양식으로 이미 파일이 생성됐거나 생성 중인 상품은 재신청 대상이 아니라 요청에서 빠진다.
  const requestableCount = getRequestableSelections(
    selectedProducts,
    kinds
  ).length;
  const alreadyRequestedCount = selectedCount - requestableCount;

  // 기본 정보와 해외 출고지는 스마트스토어 양식에만 필요하다 — KREAM 단독이면 안내 내용이 통째로 달라진다.
  const isKreamOnly = isKreamSelected && !isSmartStoreSelected;
  const isInfoComplete = isFileRequestInfoComplete(smartStoreInfo);
  const missingFieldLabels = getMissingFileRequestInfoLabelKeys(smartStoreInfo)
    .map((labelKey) => t(labelKey))
    .join(", ");

  // 양식을 둘 다 골라도 상품 기준 1회만 차감된다.
  // 실제로 파일이 생성되는 상품 = 고른 양식 중 하나라도 (재)신청 가능하고, 그 양식의 대상인 상품.
  // KREAM 미매칭 상품은 KREAM 파일에서 제외되므로 KREAM 쪽 조건에만 매칭 여부가 붙는다.
  const deductionCount = selections.filter(
    (selection) =>
      (isSmartStoreSelected &&
        isSelectionRequestable(selection, "NAVER_EXCEL")) ||
      (isKreamSelected &&
        selection.isKreamMatched &&
        isSelectionRequestable(selection, "KREAM_EXCEL"))
  ).length;

  return (
    <CustomApprovalModal
      isOpen={isOpen}
      onClose={onClose}
      // 화면보다 넓으면 왼쪽이 스크롤로도 닿지 않아 화면 폭으로 클램프한다.
      minWidth={"min(640px, 92dvw)"}
      isCentered={true}
    >
      <CustomApprovalModal.Header
        onClose={onClose}
        title={
          <Flex flexDirection={"column"} gap={"2px"}>
            <Text textStyle={"body-md-bold"} color={"#444444"}>
              {t("path_pick:basket.file_request.title")}
            </Text>
            <Text textStyle={"body-sm-regular"} color={"#8F8F8C"}>
              {t("path_pick:basket.file_request.description")}
            </Text>
          </Flex>
        }
      />
      <CustomApprovalModal.Body>
        <Flex flexDirection={"column"} gap={"12px"}>
          {/* 생성할 파일 선택 */}
          <Flex flexDirection={"column"} gap={"12px"}>
            <Flex flexDirection={"column"} gap={"2px"}>
              <Text textStyle={"body-md-bold"} color={"#1D1D1B"}>
                {t("path_pick:basket.file_request.select_title")}
              </Text>
              <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
                {t("path_pick:basket.file_request.select_description")}
              </Text>
            </Flex>
            {/* 좁은 폭에서는 양식 카드 두 장을 세로로 쌓는다. */}
            <Flex gap={"8px"} flexDirection={{ base: "column", md: "row" }}>
              <PathPickBasketCheckCard
                label={t("path_pick:basket.file_request.format_smart_store")}
                isSelected={isSmartStoreSelected}
                onToggle={() => toggleKind("NAVER_EXCEL")}
              />
              <PathPickBasketCheckCard
                label={t("path_pick:basket.file_request.format_kream")}
                suffix={
                  IS_KREAM_SELECTABLE
                    ? undefined
                    : t("path_pick:basket.file_request.format_kream_badge")
                }
                isSelected={isKreamSelected}
                isDisabled={!IS_KREAM_SELECTABLE}
                onToggle={() => toggleKind("KREAM_EXCEL")}
              />
            </Flex>
          </Flex>

          {/* KREAM을 여기서 고를 수 없는 동안에는 어디서 신청하는지 항상 알린다. */}
          {!IS_KREAM_SELECTABLE && (
            <NoticeBox
              tone={"warning"}
              title={t("path_pick:basket.file_request.kream_moved_title")}
              body={t("path_pick:basket.file_request.kream_moved_body")}
              footer={
                <Flex
                  marginTop={"4px"}
                  padding={"8px 12px"}
                  borderRadius={"6px"}
                  border={"0.5px solid #F5C79B"}
                  background={"#FFFFFF"}
                >
                  <Text textStyle={"body-sm-bold"} color={"#444440"}>
                    {t("path_pick:basket.file_request.kream_moved_path")}
                  </Text>
                </Flex>
              }
            />
          )}

          {/* 기본 정보 안내 — KREAM 단독은 기본 정보 자체가 필요 없어 문구가 다르다. */}
          {isKreamOnly && (
            <NoticeBox
              tone={"neutral"}
              title={t("path_pick:basket.file_request.kream_only_title")}
              body={t("path_pick:basket.file_request.kream_only_body")}
            />
          )}
          {isSmartStoreSelected &&
            (isInfoComplete ? (
              <NoticeBox
                tone={"neutral"}
                title={t("path_pick:basket.file_request.info_ready_title")}
                body={t("path_pick:basket.file_request.info_ready_body")}
              />
            ) : (
              <NoticeBox
                tone={"warningSubtle"}
                title={t("path_pick:basket.file_request.info_missing_title")}
                body={t("path_pick:basket.file_request.info_missing_body", {
                  fields: missingFieldLabels,
                })}
              />
            ))}

          {/* 생성 파일에 계산 판매가가 박히므로 업로드 전에 한 번 더 확인시킨다. */}
          {isSmartStoreSelected && (
            <NoticeBox
              tone={"warningSubtle"}
              title={t("path_pick:basket.file_request.price_check_title")}
              body={t("path_pick:basket.file_request.price_check_body")}
            />
          )}

          {/* 이번 신청 차감 — KREAM을 골랐을 때만 오른쪽에 대상/매칭/제외 수가 붙는다. */}
          <Flex flexDirection={"column"} gap={"6px"}>
            {/* 좁은 폭에서는 KREAM 대상/매칭/제외 수가 다음 줄로 내려간다. */}
            <Flex
              justifyContent={"space-between"}
              alignItems={"center"}
              gap={"12px"}
              flexWrap={"wrap"}
            >
              <Flex gap={"8px"} alignItems={"center"}>
                <Text textStyle={"body-md-bold"} color={"#1D1D1B"}>
                  {t("path_pick:basket.file_request.deduction_title")}
                </Text>
                <Text textStyle={"body-md-bold"} color={"#1D1D1B"}>
                  {t("path_pick:basket.file_request.deduction_count", {
                    count: deductionCount,
                  })}
                </Text>
              </Flex>
              {isKreamSelected && (
                <Flex gap={"12px"} alignItems={"center"} flexWrap={"wrap"}>
                  <Text textStyle={"body-sm-bold"} color={"#444440"}>
                    {t("path_pick:basket.file_request.kream_target_selected", {
                      count: selectedCount,
                    })}
                  </Text>
                  <Text textStyle={"body-sm-bold"} color={"#16B41C"}>
                    {t("path_pick:basket.file_request.kream_target_matched", {
                      count: kreamMatchedCount,
                    })}
                  </Text>
                  <Text textStyle={"body-sm-bold"} color={"#CC1D1D"}>
                    {t("path_pick:basket.file_request.kream_target_excluded", {
                      count: kreamExcludedCount,
                    })}
                  </Text>
                </Flex>
              )}
            </Flex>
            <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
              {t("path_pick:basket.file_request.deduction_description")}
            </Text>
            {/* 이미 생성됐거나 생성 중인 상품은 요청에서 자동으로 빠지므로 개수만 알린다. */}
            {alreadyRequestedCount > 0 && (
              <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
                {t(
                  "path_pick:basket.file_request.already_requested_description",
                  { count: alreadyRequestedCount }
                )}
              </Text>
            )}
            {isKreamSelected && (
              <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
                {t("path_pick:basket.file_request.kream_target_description")}
              </Text>
            )}
          </Flex>
        </Flex>
      </CustomApprovalModal.Body>
      <CustomApprovalModal.Footer
        cancelName={t("common:cancel")}
        confirmName={t("path_pick:basket.file_request.submit")}
        onCancel={onClose}
        onConfirm={() => onConfirm(kinds)}
        isLoading={isLoading}
        // 양식 미선택뿐 아니라 "KREAM 단독 선택 + 매칭 0개"도 차감이 0이라 신청할 수 없다.
        isConfirmDisabled={deductionCount === 0}
        confirmBtnProps={{
          width: "140px",
        }}
      />
    </CustomApprovalModal>
  );
}

export default PathPickBasketFileRequestModal;
